import { useEffect, useRef, useState } from "react";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import DataTable from "react-data-table-component";
import { Footer } from "../../Footer";
import { Button, Col, Form, Row } from "react-bootstrap";
import {
  GetWinnersAuctions,
  GetWinnersData,
} from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import dayjs from "dayjs";
import * as icons from "react-bootstrap-icons";
import { toast } from "react-toastify";

const pageOptions = [10, 20, 30, 50];
const SEARCH_DELAY_MS = 400;

// Whole dollars for a round amount, cents otherwise: a co-owner's pro-rata share of a prize rarely
// comes out round, and hiding the cents would hide money.
const formatMoney = (value) => {
  const cents = Math.round((Number(value) || 0) * 100);
  const digits = cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100);
};

// A row is one holding: exactly one of Bid / Secondary Market Price is set, and the share is shown
// next to it only when the player does not own the whole team.
const PriceCell = ({ price, ownerShip }) =>
  price == null ? (
    <span className="winners-dash">—</span>
  ) : (
    <>
      {formatMoney(price)}
      {ownerShip < 100 && <span className="winners-share">({ownerShip}%)</span>}
    </>
  );

// A 5xx or no reply has already been toasted by apiClient; anything else (a 401 from a session signed
// in before these permission codes existed, say) is reported here.
const reportFailure = (res) => {
  if (res && res.status < 500) {
    toast.error(readApiMessage(res));
  }
};

/**
 * MG-012 / AD-012: the Winners page. The winners of the signed-in Manager's - or their Admin's -
 * completed auctions; both roles see exactly the same rows, which the server scopes.
 */
export const Winners = () => {
  const [data, setData] = useState();
  const [auctions, setAuctions] = useState([]);
  const [auctionsLoaded, setAuctionsLoaded] = useState(false);
  const [auctionId, setAuctionId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perPage, setPerPage] = useState(pageOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  // Only the latest request may fill the table: typing fires one per pause, and an earlier
  // response can arrive after a later one.
  const requestId = useRef(0);

  function sidebarfun() {
    setData(!data);
  }

  useEffect(() => {
    const loadAuctions = async () => {
      const res = await GetWinnersAuctions();
      if (res?.status === 200 && Array.isArray(res.data)) {
        setAuctions(res.data);
      } else {
        reportFailure(res);
      }
      setAuctionsLoaded(true);
    };
    loadAuctions();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchText.trim();
      if (term !== searchTerm) {
        setSearchTerm(term);
        setCurrentPage(1);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText, searchTerm]);

  useEffect(() => {
    const id = ++requestId.current;
    const loadWinners = async () => {
      setLoading(true);
      const res = await GetWinnersData({
        AuctionId: auctionId === "" ? undefined : auctionId,
        SearchTerm: searchTerm || undefined,
        SortOrder: sortDesc,
        PageNumber: currentPage,
        PageSize: perPage,
      });
      if (id !== requestId.current) return;

      if (res?.status === 200) {
        setWinners(res.data?.data?.dataList || []);
        setTotalPages(res.data?.data?.totalPageSize || 0);
        setTotalRecords(res.data?.data?.totalRecordCount || 0);
      } else {
        setWinners([]);
        setTotalPages(0);
        setTotalRecords(0);
        reportFailure(res);
      }
      setLoading(false);
    };
    loadWinners();
  }, [auctionId, searchTerm, sortDesc, currentPage, perPage]);

  const handleAuctionChange = (e) => {
    setAuctionId(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchText("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const resetFilter = () => {
    setAuctionId("");
    setCurrentPage(1);
  };

  // Winning Amount is the only sortable column, so only the direction matters.
  const handleSort = (_column, sortDirection) => {
    setSortDesc(sortDirection === "desc");
    setCurrentPage(1);
  };

  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const columns = [
    {
      name: <strong>Player Name</strong>,
      selector: (row) => row?.playerName,
      wrap: true,
    },
    {
      name: <strong>Email</strong>,
      selector: (row) => row?.email,
      wrap: true,
    },
    {
      name: <strong>Team Won</strong>,
      selector: (row) => row?.teamName,
      wrap: true,
    },
    {
      name: <strong>Sport / Contest</strong>,
      selector: (row) => `${row?.sportName} / ${row?.tournamentName}`,
      wrap: true,
    },
    {
      name: <strong>Auction</strong>,
      selector: (row) => row?.auctionName,
      wrap: true,
    },
    {
      name: <strong>Auction Date</strong>,
      selector: (row) =>
        row?.auctionDate
          ? dayjs(row.auctionDate + "Z").format("MM/DD/YYYY")
          : "",
    },
    {
      name: <strong>Bid</strong>,
      cell: (row) => <PriceCell price={row?.bid} ownerShip={row?.ownerShip} />,
    },
    {
      name: <strong>Secondary Market Price</strong>,
      cell: (row) => (
        <PriceCell price={row?.secondaryMarketPrice} ownerShip={row?.ownerShip} />
      ),
      wrap: true,
    },
    {
      id: "winningAmount",
      name: <strong>Winning Amount</strong>,
      selector: (row) => row?.winningAmount,
      cell: (row) => (
        <span className="winners-amount">{formatMoney(row?.winningAmount)}</span>
      ),
      sortable: true,
    },
  ];

  const isFiltered = Boolean(searchTerm) || auctionId !== "";
  const noWinnersYet = (auctionsLoaded && auctions.length === 0) || !isFiltered;

  const emptyState = noWinnersYet ? (
    <div className="winners-empty">
      <icons.Award className="winners-empty-icon" />
      <p>
        No winners yet. Winners will appear here once one of your auctions is
        completed.
      </p>
    </div>
  ) : (
    <div className="winners-empty">
      <icons.Search className="winners-empty-icon" />
      <p>No winners match your search or filter.</p>
      <div className="winners-empty-actions">
        {searchTerm && (
          <button type="button" className="btn-outline-winners" onClick={clearSearch}>
            Clear search
          </button>
        )}
        {auctionId !== "" && (
          <button type="button" className="btn-outline-winners" onClick={resetFilter}>
            Reset filter
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div
          className={
            data ? "dashboard-content full-contnet" : "dashboard-content"
          }
        >
          <Row>
            <Col md={6}>
              <h1 className="heading-dash">Winners</h1>
            </Col>
          </Row>

          <div className="group_data_table winners">
            <div className="winners-toolbar">
              <div className="winners-filter">
                <label htmlFor="winners-auction">Auction:</label>
                <Form.Select
                  id="winners-auction"
                  value={auctionId}
                  onChange={handleAuctionChange}
                >
                  <option value="">All auctions</option>
                  {auctions.map((auction) => (
                    <option key={auction.auctionId} value={auction.auctionId}>
                      {auction.auctionName}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="search-input d-flex align-items-center">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {searchText && (
                  <button className="btn btn-clear" onClick={clearSearch}>
                    <span>&times;</span>
                  </button>
                )}
              </div>
            </div>

            <div>
              <DataTable
                columns={columns}
                data={winners}
                progressPending={loading}
                persistTableHead
                sortServer
                onSort={handleSort}
                defaultSortFieldId="winningAmount"
                defaultSortAsc={false}
                noDataComponent={emptyState}
              />
              {totalRecords > pageOptions[0] && (
                <Row>
                  <Col md={12} className="text-right">
                    <div className="pagination-controls">
                      <label>Rows per page: </label>
                      <select
                        id="itemsPerPage"
                        value={perPage}
                        onChange={handlePerPageChange}
                      >
                        {pageOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>

                      <Button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <icons.ArrowLeft></icons.ArrowLeft>
                      </Button>
                      <span>
                        {" "}
                        {currentPage} of {totalPages}{" "}
                      </span>
                      <Button
                        onClick={handleNextPage}
                        disabled={currentPage >= totalPages}
                      >
                        <icons.ArrowRight></icons.ArrowRight>
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
    </>
  );
};
